import React, { PureComponent } from "react";
import styled from 'styled-components';
import { Loader } from 'react-loaders';
import { getRequest, batchRequest, putRequest, deleteRequest } from "../../api/ApiService";
import { isSurveyFinished } from '../../api/Surveys';
import { clearOnboardingSetupCache } from '../components/onboarding/onboardingRouting';
import Auth from '../../routes/Auth';
import DateTimePicker from 'react-datetime';
import moment from 'moment';
import ErrorAlert from '../../components/alerts/error';
import DecisionsAlert from '../../components/alerts/decisions';

const Wrapper = styled.div`
  background: #f5f6f7;
  padding: 40px;
  flex: 1;

  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 8px;
  max-width: 900px;
  margin: 0 auto;
`;

const CardHeader = styled.div`
  padding: 20px 24px 0;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 23px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 4px;
`;

const PageSubtitle = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.6);
  margin: 0 0 16px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #D5D9DD;
  margin: 0;
`;

const TableWrapper = styled.div`
  padding: 16px 24px 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Mukta Vaani';
`;

const TableHead = styled.thead`
  th {
    font-family: 'Mukta Vaani';
    font-weight: 700;
    font-size: 14px;
    line-height: 24px;
    color: rgba(0, 27, 59, 0.94);
    text-align: left;
    padding: 8px 2px 12px;
  }
`;

const TableBody = styled.tbody`
  td {
    padding: 8px 2px;
    vertical-align: middle;
  }
`;

const UpdateCell = styled.td`
  padding-left: 2px !important;
  padding-right: 2px !important;
`;

const TableRow = styled.tr`
  & + & {
    td {
      padding-top: 16px;
    }
  }
`;

const EvaluationName = styled.span`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #494e50;
`;

const EditIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  vertical-align: middle;
  color: #494e50;
  font-size: 14px;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }

  i {
    font-size: 14px;
  }
`;

const DeleteIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  vertical-align: middle;
  color: #494e50;
  font-size: 14px;

  &:hover {
    color: #d34c4c;
  }

  i {
    font-size: 14px;
  }
`;

const DatePickerCell = styled.div`
  min-width: 220px;
  max-width: 239px;
`;

const UpdateButton = styled.button`
  background: #F5F6F7;
  border-radius: 4px;
  border: none;
  padding: 6.5px 9.5px;
  color: rgba(0, 27, 59, 0.38);
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  pointer-events: none;
  min-width: 78px;
  cursor: default;

  ${p => p.isActive && `
    background: #2196F3;
    color: #fff;
    pointer-events: auto;
    cursor: pointer;
  `}
`;

const ButtonLoader = styled.div`
  width: 100%;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  > .loader {
    margin-top: 0 !important;
    margin-left: 0 !important;

    .loader-inner div {
      width: 15px;
      height: 15px;
      border-color: #fff !important;
    }
  }
`;

const CompletionCellWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CompletionBarOuter = styled.div`
  width: 88px;
  height: 24px;
  border-radius: 4px;
  background: #E0E0E0;
  position: relative;
  overflow: hidden;
  border: 1px solid #D5D9DD;
  cursor: pointer;
`;

const CompletionBarFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #2DA548;
  border-radius: ${props => props.percentage >= 100 ? '4px' : '4px 0 0 4px'};
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const CompletionLabel = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  z-index: 1;
`;

const CompletionTooltip = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px 16px;
  z-index: 100;
  min-width: 220px;
  max-height: 250px;
  overflow-y: auto;

  &:after {
    content: '';
    position: absolute;
    top: -6px;
    right: 24px;
    width: 10px;
    height: 10px;
    background: #ffffff;
    border-left: 1px solid #D5D9DD;
    border-top: 1px solid #D5D9DD;
    transform: rotate(45deg);
  }
`;

const TooltipTitle = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 8px;
  border-bottom: 1px solid #ECEDEF;
  padding-bottom: 6px;
`;

const TooltipStudentName = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.8);
  margin: 0;
`;

const EmptyStateWrapper = styled.div`
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const EmptyStateDescription = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #000000;
  text-align: center;
  margin: 0;
`;

const AddEvaluationButton = styled.button`
  background: #ffffff;
  border: 1px solid #3A507B;
  border-radius: 4px;
  padding: 4px 8px;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  color: #494e50;
  cursor: pointer;
  text-align: center;
  min-width: 200px;

  &:hover {
    background: #f5f6f7;
  }
`;

/* Modal Styled Components */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.08), 0px 20px 32px 0px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 1137px;
  max-height: 85vh;
  overflow-y: auto;
  padding: 40px 96px 40px;
  position: relative;
`;

const ModalHeader = styled.div`
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 30px;
  line-height: 24px;
  color: #2A4169;
  margin: 0 0 8px;
  display: inline-block;
`;

const ModalNameInput = styled.div`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  margin-left: 16px;
  display: inline-flex;
  min-width: 400px;
`;

const ModalNameText = styled.span`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 20px;
  line-height: 2px;
  color: #2A4169;
`;

const ModalDescription = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  color: #2A4169;
  margin: 12px 0 24px;
`;

const SectionTitle = styled.h3`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  text-decoration: underline;
  margin: 24px 0 12px;

  span {
    font-weight: 400;
    font-size: 16px;
    text-decoration: none;
  }
`;

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const QuestionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;


const QuestionText = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;


const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: rgba(0, 27, 59, 0.6);
  padding: 4px 8px;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

/* Delete-confirmation modal — smaller than the questions modal */

const ConfirmModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.08), 0px 20px 32px 0px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 460px;
  padding: 28px 32px;
`;

const ConfirmTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const ConfirmBody = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 24px;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 8px 16px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;

  &:hover {
    background: #f5f6f7;
  }
`;

const ConfirmDeleteButton = styled.button`
  background: #d34c4c;
  border: 1px solid #d34c4c;
  border-radius: 4px;
  padding: 8px 16px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: #b23e3e;
    border-color: #b23e3e;
  }

  &:disabled {
    background: #e5a3a3;
    border-color: #e5a3a3;
    cursor: default;
  }
`;

/* Question grouping helper */

const SECTION_DEFS = [
  {
    key: 'selfReflection',
    label: 'Self-Reflection',
    filter: q => q.category === 'self_assessment',
  },
  {
    key: 'teammateRating',
    label: 'Teammate Rating',
    suffix: ' (1= Strongly Disagree, 5= Strongly Agree)',
    filter: q => q.category === 'collaboration' && q.answer_type === 'rating',
  },
  {
    key: 'teammateReflections',
    label: 'Teammate Reflections',
    filter: q => q.answer_type === 'freeform' && q.category !== 'self_assessment',
  },
];

const PART_LABELS = ['I', 'II', 'III', 'IV'];

function groupQuestions(questions) {
  if (!questions || questions.length === 0) return [];

  const usedIds = new Set();
  const sections = [];

  SECTION_DEFS.forEach(def => {
    const matched = questions
      .filter(q => {
        if (usedIds.has(q.id)) return false;
        return def.filter(q);
      })
      .sort((a, b) => a.id - b.id);

    matched.forEach(q => usedIds.add(q.id));

    if (matched.length > 0) {
      sections.push({
        key: def.key,
        label: def.label,
        suffix: def.suffix || '',
        questions: matched,
      });
    }
  });

  return sections;
}

class InstructorTeamEvaluation extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      surveys: [],
      totalStudents: 0,
      // Per-survey set of user ids whose peer-score row has
      // completion === true. Populated from /v2/games/peer-score/
      // on page load (one call per survey). The bar uses .size,
      // the hover popup uses set membership.
      completedUsersBySurvey: {},
      courseMembers: [],
      modalSurvey: null,
      changedSurveys: [],
      changedFieldsBySurvey: {},
      loadingButtonSurvey: 0,
      showSuccess: false,
      successfullyChangedSurveyName: '',
      showError: false,
      errorTitle: '',
      errorMessage: '',
      hoveredSurveyId: null,
      deleteTargetSurvey: null,
      isDeleting: false,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    getRequest(`${apiUrl}v2/forms/surveys/?course=${Auth.getCourseId()}`)
      .then(res => {
        const surveys = res.body.results || res.body;

        // Stage-first ordering: Initial peer eval always appears above
        // Final. Anything else (unknown stage) sorts last but keeps
        // relative order stable via index tiebreak.
        const stageOrder = { initial: 0, final: 1 };
        const withIndex = (Array.isArray(surveys) ? surveys : [])
          .map((s, i) => ({ s, i }));
        const ordered = withIndex
          .sort((a, b) => {
            const aStage = String((a.s && a.s.stage) || '').toLowerCase();
            const bStage = String((b.s && b.s.stage) || '').toLowerCase();
            const ao = stageOrder[aStage] != null ? stageOrder[aStage] : 2;
            const bo = stageOrder[bStage] != null ? stageOrder[bStage] : 2;
            if (ao !== bo) return ao - bo;
            return a.i - b.i;
          })
          .map(x => x.s);
        this.setState({
          loaded: true,
          surveys: ordered,
        });

        this.fetchCompletionData(ordered);
      })
      .catch(err => {
        this.setState({ loaded: true, surveys: [] });
      });
  }

  // Completion data now comes from /v2/games/peer-score/<survey_id>/.
  // A "completed" student = one row on that endpoint whose
  // `completion === true`. The course-members list is the ground
  // truth for how many students exist, and the bar count is capped
  // at that total so it can't exceed it. The hover popup is just
  // the set difference of (course members − completed user ids).
  fetchCompletionData = (surveys) => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const surveyList = surveys || this.state.surveys || [];
    const peerScoreUrls = surveyList.map(
      s => `${apiUrl}v2/games/peer-score/${s.id}/`
    );

    batchRequest([
      `${apiUrl}course_members/?type=1`,
      ...peerScoreUrls,
    ])
      .then(responses => {
        const membersRes = responses[0];
        const membersData = (membersRes && membersRes.body && (membersRes.body.results || membersRes.body)) || [];
        const courseMembers = Array.isArray(membersData) ? membersData : [];
        const totalStudents = courseMembers.length;

        const completedUsersBySurvey = {};
        surveyList.forEach((survey, i) => {
          const res = responses[1 + i];
          const body = (res && res.body) || {};
          const rows = Array.isArray(body) ? body : (body.results || []);
          const set = new Set();
          rows.forEach(row => {
            if (row && row.completion === true) {
              const uid = row.user_id != null ? row.user_id : row.user;
              if (uid != null) set.add(uid);
            }
          });
          completedUsersBySurvey[survey.id] = set;
        });

        this.setState({
          totalStudents,
          completedUsersBySurvey,
          courseMembers,
        });
      })
      .catch(err => {});
  }

  getIncompleteStudentNames = (surveyId) => {
    const { completedUsersBySurvey, courseMembers } = this.state;
    const completedIds = completedUsersBySurvey[surveyId] || new Set();

    return courseMembers
      .filter(member => {
        const memberId = member.user_id || member.user || member.id;
        return !completedIds.has(memberId);
      })
      .map(member => `${member.first_name || ''} ${member.last_name || ''}`.trim())
      .filter(name => name.length > 0);
  }

  onDateChange = (datetime, surveyId, field) => {
    if (typeof datetime === "string") {
      return;
    }

    const { changedSurveys, changedFieldsBySurvey } = this.state;

    const surveys = [...this.state.surveys];
    const index = surveys.findIndex(s => s.id === surveyId);

    if (index >= 0) {
      surveys[index] = { ...surveys[index], [field]: datetime };

      let newChangedSurveys = [...changedSurveys];
      if (!newChangedSurveys.includes(surveyId)) {
        newChangedSurveys.push(surveyId);
      }

      const existingFields = changedFieldsBySurvey[surveyId] || {};
      const newChangedFields = {
        ...changedFieldsBySurvey,
        [surveyId]: { ...existingFields, [field]: true },
      };

      this.setState({
        surveys,
        changedSurveys: newChangedSurveys,
        changedFieldsBySurvey: newChangedFields,
      });
    }
  }

  onUpdate = (surveyId) => {
    const {
      surveys,
      changedFieldsBySurvey,
    } = this.state;

    const survey = surveys.find(s => s.id === surveyId);
    if (!survey) return;

    const surveyChangedFields = changedFieldsBySurvey[surveyId] || {};
    const startDate = moment.isMoment(survey.start) ? survey.start : moment(survey.start);
    const endDate = moment.isMoment(survey.end) ? survey.end : moment(survey.end);
    const currentDateTime = moment();

    if (endDate.isBefore(startDate)) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Start Time > End Time',
        errorMessage: 'The start date/time cannot be greater than the end date/time.',
      });
      return;
    }

    if (surveyChangedFields.start && startDate.isBefore(currentDateTime)) {
      this.setState({
        showError: true,
        errorTitle: 'Error | In the Past',
        errorMessage: 'The start date/time cannot be set in the past.',
      });
      return;
    }

    if (surveyChangedFields.end && endDate.isBefore(currentDateTime)) {
      this.setState({
        showError: true,
        errorTitle: 'Error | In the Past',
        errorMessage: 'The end date/time cannot be set in the past.',
      });
      return;
    }

    this.setState({ loadingButtonSurvey: surveyId });

    const { changedSurveys } = this.state;

    putRequest(
      `${process.env.REACT_APP_API_URL}v2/forms/surveys/${surveyId}/modify/`
    )
      .send({
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      })
      .then(res => {
        const updatedChangedFields = { ...this.state.changedFieldsBySurvey };
        delete updatedChangedFields[surveyId];

        this.setState({
          loadingButtonSurvey: 0,
          changedSurveys: changedSurveys.filter(id => id !== surveyId),
          changedFieldsBySurvey: updatedChangedFields,
          showSuccess: true,
          successfullyChangedSurveyName: survey.name || 'Evaluation',
        });
      })
      .catch(err => {
        this.setState({ loadingButtonSurvey: 0 });

        if (
          err.response &&
          err.response.status === 400 &&
          err.response.body &&
          "non_field_errors" in err.response.body &&
          err.response.body["non_field_errors"].length > 0
        ) {
          this.setState({
            showError: true,
            errorTitle: 'Error | Server Error',
            errorMessage: err.response.body["non_field_errors"][0],
          });
        }
      });
  }

  hideSuccessAlert = () => {
    this.setState({ showSuccess: false });
  }

  hideErrorAlert = () => {
    this.setState({ showError: false });
  }

  getCompletionCounts = (survey) => {
    const { totalStudents, completedUsersBySurvey } = this.state;
    const set = completedUsersBySurvey[survey.id];
    const completed = set ? set.size : 0;
    // Cap at totalStudents so the bar can't visually exceed the
    // denominator if peer-score ever returns extra rows.
    return { completed: Math.min(completed, totalStudents), total: totalStudents };
  }

  openModal = (survey) => {
    this.setState({ modalSurvey: survey });
  }

  closeModal = () => {
    this.setState({ modalSurvey: null });
  }

  openDeleteConfirmation = (survey) => {
    this.setState({ deleteTargetSurvey: survey });
  }

  closeDeleteConfirmation = () => {
    if (this.state.isDeleting) return;
    this.setState({ deleteTargetSurvey: null });
  }

  confirmDelete = () => {
    const { deleteTargetSurvey, surveys } = this.state;
    if (!deleteTargetSurvey) return;

    const surveyId = deleteTargetSurvey.id;
    this.setState({ isDeleting: true });

    deleteRequest(`${process.env.REACT_APP_API_URL}v2/forms/surveys/${surveyId}/`)
      .then(() => {
        // Invalidate the onboarding gate's positive hasSurvey cache —
        // otherwise the gate would keep skipping the survey step even
        // though this instructor may have just deleted their only one.
        clearOnboardingSetupCache();
        this.setState({
          surveys: surveys.filter(s => s.id !== surveyId),
          deleteTargetSurvey: null,
          isDeleting: false,
        });
      })
      .catch(err => {
        this.setState({
          isDeleting: false,
          deleteTargetSurvey: null,
          showError: true,
          errorTitle: 'Error | Delete Failed',
          errorMessage: 'The evaluation could not be deleted. Please try again.',
        });
      });
  }

  renderDeleteConfirmation = () => {
    const { deleteTargetSurvey, isDeleting } = this.state;
    if (!deleteTargetSurvey) return null;

    const name = deleteTargetSurvey.name || 'this evaluation';

    return (
      <ModalOverlay onClick={this.closeDeleteConfirmation}>
        <ConfirmModalContainer onClick={e => e.stopPropagation()}>
          <ConfirmTitle>Delete evaluation?</ConfirmTitle>
          <ConfirmBody>
            Are you sure you want to delete <b>{name}</b>? This cannot be undone.
          </ConfirmBody>
          <ConfirmActions>
            <CancelButton onClick={this.closeDeleteConfirmation} disabled={isDeleting}>
              Cancel
            </CancelButton>
            <ConfirmDeleteButton onClick={this.confirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </ConfirmDeleteButton>
          </ConfirmActions>
        </ConfirmModalContainer>
      </ModalOverlay>
    );
  }

  renderModal = () => {
    const { modalSurvey } = this.state;
    if (!modalSurvey) return null;

    const sections = groupQuestions(modalSurvey.questions);

    return (
      <ModalOverlay onClick={this.closeModal}>
        <ModalContainer onClick={e => e.stopPropagation()}>
          <CloseButton onClick={this.closeModal}>&times;</CloseButton>
          <ModalHeader>
            <ModalTitle>Name</ModalTitle>
            <ModalNameInput>
              <ModalNameText>{modalSurvey.name}</ModalNameText>
            </ModalNameInput>
          </ModalHeader>
          <ModalDescription>
            This peer evaluation requires students to complete a self-reflection, rate each teammate, and provide constructive feedback for each team member.
          </ModalDescription>

          {(() => {
            let runningIndex = 0;
            return sections.map((section, idx) => (
              <div key={section.key}>
                <SectionTitle>
                  Part {PART_LABELS[idx]} | {section.label}
                  {section.suffix && <span>{section.suffix}</span>}
                </SectionTitle>
                <QuestionList>
                  {section.questions.map(question => {
                    runningIndex++;
                    return (
                      <QuestionItem key={question.id}>
                        <QuestionText>{runningIndex}. {question.text.replace(/<Insert User FirstName>/gi, 'this teammate').replace(/<insert user firstname>/gi, 'this teammate')}</QuestionText>
                      </QuestionItem>
                    );
                  })}
                </QuestionList>
              </div>
            ));
          })()}

          {sections.length === 0 && (
            <ModalDescription>No questions configured for this evaluation.</ModalDescription>
          )}

        </ModalContainer>
      </ModalOverlay>
    );
  }

  render() {
    const {
      loaded,
      surveys,
      changedSurveys,
      loadingButtonSurvey,
      showSuccess,
      successfullyChangedSurveyName,
      showError,
      errorTitle,
      errorMessage,
      hoveredSurveyId,
    } = this.state;

    if (!loaded) {
      return (
        <Wrapper>
          <Loader type="ball-scale-ripple" active />
        </Wrapper>
      );
    }

    const isIAS = Auth.isAssistantOrCoInstructor();

    return (
      <Wrapper>
        {showError && (
          <ErrorAlert
            title={errorTitle}
            message={errorMessage}
            onHide={this.hideErrorAlert}
          />
        )}
        {showSuccess && (
          <DecisionsAlert
            type='success'
            title='Success!'
            message={`${successfullyChangedSurveyName} has been successfully updated!`}
            onHide={this.hideSuccessAlert}
          />
        )}
        <Card>
          <CardHeader>
            <PageTitle>Team Evaluation</PageTitle>
            <PageSubtitle>
              Review the questions, monitor completions, assess results
            </PageSubtitle>
          </CardHeader>
          <Divider />
          {surveys.length > 0 ? (
            <TableWrapper>
              <Table>
                <TableHead>
                  <tr>
                    <th>Evaluation</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style={{ width: '1px', padding: '8px 2px 12px' }}></th>
                    <th style={{ textAlign: 'center' }}>Completion %</th>
                  </tr>
                </TableHead>
                <TableBody>
                  {surveys.map((survey, index) => {
                    const { completed, total } = this.getCompletionCounts(survey);
                    const isChanged = changedSurveys.includes(survey.id);
                    const isLoading = loadingButtonSurvey === survey.id;
                    const updateButtonText = isChanged ? 'Update' : 'Updated';
                    const incompleteNames = hoveredSurveyId === survey.id
                      ? this.getIncompleteStudentNames(survey.id)
                      : [];

                    return (
                      <TableRow key={survey.id} className={survey.start || survey.end ? 'with-highlighted-time' : ''}>
                        <td>
                          <EvaluationName>
                            {survey.name || `Team Evaluation ${index + 1}`}
                          </EvaluationName>
                          {!isIAS && (
                            <EditIcon title="Edit evaluation" onClick={() => this.openModal(survey)}>
                              <i className="fa fa-pencil" />
                            </EditIcon>
                          )}
                          {!isIAS && (
                            <DeleteIcon title="Delete evaluation" onClick={() => this.openDeleteConfirmation(survey)}>
                              <i className="fa fa-trash" />
                            </DeleteIcon>
                          )}
                        </td>
                        <td>
                          <DatePickerCell>
                            <DateTimePicker
                              dateFormat="ddd, MMM DD YYYY,"
                              timeFormat="hh:mm a"
                              inputProps={{
                                placeholder: "Select Date & Time",
                                className: "instructor-round-datetime-selector",
                                disabled: isIAS,
                                readOnly: isIAS,
                              }}
                              value={survey.start ? moment(survey.start) : ''}
                              onChange={dt => isIAS ? null : this.onDateChange(dt, survey.id, 'start')}
                            />
                          </DatePickerCell>
                        </td>
                        <td>
                          <DatePickerCell>
                            <DateTimePicker
                              dateFormat="ddd, MMM DD YYYY,"
                              timeFormat="hh:mm a"
                              inputProps={{
                                placeholder: "Select Date & Time",
                                className: "instructor-round-datetime-selector",
                                disabled: isIAS,
                                readOnly: isIAS,
                              }}
                              value={survey.end ? moment(survey.end) : ''}
                              onChange={dt => isIAS ? null : this.onDateChange(dt, survey.id, 'end')}
                            />
                          </DatePickerCell>
                        </td>
                        <UpdateCell>
                          <UpdateButton
                            isActive={!isIAS && isChanged}
                            onClick={() => isIAS ? null : this.onUpdate(survey.id)}
                          >
                            {isLoading ? (
                              <ButtonLoader>
                                <Loader type="ball-scale-ripple" active />
                              </ButtonLoader>
                            ) : (
                              <span>{updateButtonText}</span>
                            )}
                          </UpdateButton>
                        </UpdateCell>
                        <td>
                          <CompletionCellWrapper
                            onMouseEnter={() => this.setState({ hoveredSurveyId: survey.id })}
                            onMouseLeave={() => this.setState({ hoveredSurveyId: null })}
                          >
                            <CompletionBarOuter>
                              <CompletionBarFill percentage={total > 0 ? (completed / total) * 100 : 0} />
                              <CompletionLabel>{completed}/{total}</CompletionLabel>
                            </CompletionBarOuter>
                            {hoveredSurveyId === survey.id && incompleteNames.length > 0 && (
                              <CompletionTooltip>
                                <TooltipTitle>Not Yet Completed</TooltipTitle>
                                {incompleteNames.map((name, i) => (
                                  <TooltipStudentName key={i}>{i + 1}. {name}</TooltipStudentName>
                                ))}
                              </CompletionTooltip>
                            )}
                          </CompletionCellWrapper>
                        </td>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableWrapper>
          ) : (
            <EmptyStateWrapper>
              <EmptyStateDescription>
                A simple way for students to reflect and share feedback with what's working well on teams and what could be improved.
              </EmptyStateDescription>
              {!isIAS && (
                <AddEvaluationButton>
                  Add a Team Evaluation
                </AddEvaluationButton>
              )}
            </EmptyStateWrapper>
          )}
        </Card>
        {this.renderModal()}
        {this.renderDeleteConfirmation()}
      </Wrapper>
    );
  }
}

export default InstructorTeamEvaluation;



// WEBPACK FOOTER //
// ./src/instructor/containers/team-evaluation.js